import { links } from '../links'

/**
 * Sämtliche Texte der Carrier-Hub-Landingpage.
 *
 * Die Inhalte sind 1:1 aus `original_assets/carrier.pdf` übernommen. Ausnahme
 * sind die vier eingeklappten FAQ-Antworten – sie liegen im Entwurf nicht vor
 * und sind unten als Entwurf markiert (siehe `TODO`).
 */

export const hero = {
  title: ['Weniger Dark.', 'Mehr Fiber.'],
}

export const intro = {
  eyebrow: 'Zentrale Plattform für Dark-Fiber',
  title: ['Dark-Fiber-Verbindungen', 'über mehrere Netze hinweg finden.'],
  body: [
    'Der Carrier Hub bündelt regionale und bundesweite Glasfasernetze in einer zentralen Streckensuche.',
    'So entsteht schnell eine erste Einschätzung möglicher Verbindungen über mehrere Netze hinweg.',
    'Die Plattform führt zugelieferte Trassen- und Adressdaten zentral zusammen und macht sie für eine einfache Streckensuche nutzbar.',
  ],
  lead: 'Eine Plattform für beide Seiten. Abfrager suchen lokale, regionale oder bundesweite Dark-Fiber-Verbindungen. Anbieter bringen ihre Netzdaten geschützt in die Streckensuche ein und erhalten passende Anfragen.',
  leadStrong: 'Eine Plattform für beide Seiten.',
}

export type Party = {
  name: string
  points: {
    text: string
    /** Kursive Klammer-Ergänzung darunter. */
    note?: string
    /** Weitere Zeile unterhalb der Klammer. */
    suffix?: string
  }[]
}

export const parties: { top: Party; left: Party; right: Party } = {
  top: {
    name: 'Abfrager',
    points: [
      { text: 'potenzieller Kunde für eine Dark-Fiber-Verbindung' },
      { text: 'benötigt ein oder mehrere Trassen', note: 'lokal/regional oder bundesweit' },
    ],
  },
  left: {
    name: 'GU-Partner',
    points: [
      {
        text: 'Eingabe Netz',
        note: 'bundesweites / regionales',
        suffix: 'inkl. Preisparameter in den HUB',
      },
      { text: 'erhält Abfragen vom HUB', note: 'bearbeitet Leads / Angebote / Aufträge' },
    ],
  },
  right: {
    name: 'Netz-Partner',
    points: [
      { text: 'bringt sein Netz direkt in den HUB' },
      { text: 'Vermarktung erfolgt fallweise über GU' },
    ],
  },
}

export const audiences = [
  {
    id: 'abfrager',
    role: 'Abfrager',
    title: ['Sie suchen eine lokale,', 'regionale oder bundesweite', 'Dark-Fiber-Verbindung?'],
    body: 'Geben Sie Start- und Endpunkt ein und erhalten Sie eine erste Einschätzung möglicher Strecken.',
    cta: { label: 'Strecke abfragen', href: links.routeQuery, variant: 'primary' as const },
  },
  {
    id: 'anbieter',
    role: 'Anbieter',
    title: ['Sie möchten Ihr Netz für', 'zusätzliche Anfragen', 'nutzbar machen?'],
    body: 'Bringen Sie Ihre Netz- und Adressdaten geschützt in den Carrier Hub ein und werden Sie bei passenden Streckensuchen berücksichtigt.',
    cta: { label: 'Netz einbringen', href: links.networkOffer, variant: 'secondary' as const },
  },
]

export const trust = {
  eyebrow: 'Vertrauen & Sicherheit',
  title: 'Ihre Netzdaten bleiben geschützt',
  cards: [
    {
      eyebrow: 'Geschützter Datentransfer',
      title: ['Sichere', 'Übertragung'],
      body: 'Netz- und Adressdaten werden über einen gesicherten SFTP-Server angeliefert und aktualisiert.',
    },
    {
      eyebrow: 'Human Overview statt offener Zugriff',
      title: ['Überprüfung', 'der Abfrager'],
      body: 'Abfrager durchlaufen einen dokumentierten Registrierungsprozess, bevor sie die Plattform nutzen können. Auffälliges Abfrageverhalten wird unterbunden.',
    },
    {
      eyebrow: 'Security by Design',
      title: ['Geschützte', 'Verarbeitung'],
      body: 'vitroconnect unterliegt KRITIS und selbstverständlich behandeln wir die Daten unserer Partner wie unsere eigenen. Weiterhin ist vitroconnect ISO/IEC 27001 zertifiziert.',
    },
    {
      eyebrow: 'Keine Offenlegung Ihrer Infrastruktur',
      title: ['Gefahrlose', 'Marktdarstellung'],
      body: 'Vertrauliche geografische Informationen werden nicht exakt dargestellt; Einstiegspunkte und Trassenführungen werden bewusst ungenau angezeigt.',
    },
  ],
}

export const process = {
  eyebrow: 'So funktioniert die Streckenabfrage',
  title: ['Von der Suche', 'bis zur konkreten Anfrage.'],
  body: 'Start- und Endpunkt eingeben, mögliche Verbindungen prüfen und die Anfrage über den Carrier Hub weiterführen. Anschließend werden Machbarkeit und Konditionen geprüft und ein konkretes Angebot erstellt.',
  actor: 'Abfrager',
  /**
   * Phasenbalken über den Schritten. Breiten aus dem Entwurf
   * (Element 530/531/532: 161 / 601 / 161 Einheiten in einer Gruppe von 938).
   */
  phases: [
    { label: 'Anfrage', width: '17.02%' },
    { label: 'Planung', width: '63.83%' },
    { label: 'Beauftragung', width: '17.02%' },
  ],
  steps: [
    {
      title: 'Strecke suchen',
      body: 'Start- und Endpunkt der benötigten Verbindung eingeben',
    },
    {
      title: 'Möglichkeiten prüfen',
      body: 'Mögliche Gesamt- und Alternativstrecken über die eingebundenen Netze erhalten.',
    },
    {
      title: 'Anfrage weiterführen',
      body: 'Strecke speichern und zur weiteren Prüfung und Angebotserstellung an den zuständigen GU-Partner übergeben.',
    },
  ],
}

export const together = {
  eyebrow: 'Carrier Hub für Abfrager und Anbieter',
  title: ['Streckenbedarf trifft Netzkapazität.', 'Nutzen für beide Seiten.'],
  columns: [
    {
      role: 'Abfrager',
      cta: { label: 'Strecke abfragen', href: links.routeQuery, variant: 'primary' as const },
      benefits: [
        { text: 'Zentrale Anfragen statt vieler Einzelanfragen', strong: 'Zentrale Anfragen' },
        { text: 'Schnelle Ersteinschätzung möglicher Strecken', strong: 'Schnelle Ersteinschätzung' },
        { text: 'Weniger Recherche- und Planungsaufwand', strong: 'Weniger Recherche-' },
        { text: 'Gebündelte Beauftragung mit einheitlichen Konditionen und SLAs', strong: 'Gebündelte Beauftragung' },
      ],
    },
    {
      role: 'Anbieter',
      cta: { label: 'Netz einbringen', href: links.networkOffer, variant: 'secondary' as const },
      benefits: [
        { text: 'Zusätzlicher digitaler Vertriebskanal', strong: 'Vertriebskanal' },
        { text: 'Neue qualifizierte Leads und Aufträge', strong: 'qualifizierte Leads' },
        { text: 'Vorhandene Netzinfrastruktur besser auslasten', strong: 'Vorhandene Netzinfrastruktur' },
        { text: 'Netzdaten geschützt in die Streckensuche einbringen', strong: 'Netzdaten geschützt' },
      ],
    },
  ],
}

export const faq = {
  title: 'FAQ‘s',
  items: [
    {
      question: 'Wie läuft eine Streckenabfrage ab?',
      answer:
        'Der Abfrager gibt Start- und Endpunkt ein. Der Carrier Hub ermittelt auf Basis der hinterlegten Netzdaten mögliche Verbindungen und zeigt Alternativen auf. Wird daraus ein Lead generiert, wird dieser zur weiteren Prüfung und Angebotserstellung an den zuständigen GU-Partner weitergeleitet.',
    },
    // TODO (Kunde): Die folgenden vier Antworten sind im Entwurf eingeklappt und
    // liegen nicht als Text vor. Die Formulierungen unten sind aus den Aussagen
    // der Seite abgeleitet und vor dem Livegang fachlich zu prüfen.
    {
      question: 'Wie werden mögliche Strecken berechnet?',
      answer:
        'Grundlage sind die von den Netz- und GU-Partnern zugelieferten Trassen- und Adressdaten, die der Carrier Hub zentral zusammenführt. Daraus werden mögliche Gesamt- und Alternativstrecken über die eingebundenen Netze ermittelt und als erste Einschätzung angezeigt.',
    },
    {
      question: 'Ist die Streckenverfügbarkeit verbindlich?',
      answer:
        'Nein. Das Ergebnis der Streckensuche ist eine erste Einschätzung möglicher Verbindungen. Machbarkeit und Konditionen werden anschließend vom zuständigen GU-Partner geprüft; verbindlich ist erst das daraus erstellte Angebot.',
    },
    {
      question: 'Werden meine Netz- und Trassendaten für andere Partner sichtbar?',
      answer:
        'Nein. Vertrauliche geografische Informationen werden nicht exakt dargestellt und Einstiegspunkte bewusst ungenau angezeigt. Die Datenanlieferung erfolgt über einen gesicherten SFTP-Server, Zugriffe erfolgen nach dem Need-to-know-Prinzip.',
    },
    {
      question: 'Wer kann Kunden-, Anfrage- und Lead-Daten sehen?',
      answer:
        'Anfragen und Leads werden ausschließlich an den zuständigen GU-Partner weitergeleitet. Administrative Zugriffe erfordern MFA und eindeutig zuordenbare Zugänge. Carrier Hub unterliegt Kritis und dem vitroconnect-ISMS; auch Subdienstleister sind entsprechend gebunden.',
    },
  ],
}

export const finalCta = {
  eyebrow: 'Carrier Hub',
  title: 'Weniger Dark. Mehr Fiber.',
  body: 'Starten Sie direkt mit einer Streckenabfrage oder nehmen Sie Kontakt mit uns auf, wenn Sie Ihr Netz in den Carrier Hub einbringen möchten.',
  ctas: [
    { label: 'Strecke abfragen', href: links.routeQuery, variant: 'primary' as const },
    { label: 'Netz einbringen', href: links.networkOffer, variant: 'secondary' as const },
  ],
}

export const footerLinks = [
  { label: 'CAP', href: links.cap },
  { label: 'Impressum', href: links.imprint },
  { label: 'Datenschutz', href: links.privacy },
]

export const ui = {
  skipToContent: 'Zum Inhalt springen',
  toAudiences: 'Zu den Angeboten für Abfrager und Anbieter',
}
